import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/videos';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [showSearchSuggest, setShowSearchSuggest] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      setShowSearchSuggest(false);
    }
  };

  const isHome = location.pathname === '/';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <nav className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 max-w-[1400px] mx-auto">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className="text-2xl font-bold text-primary-500 font-heading">91tv</span>
            <span className={`text-sm hidden sm:inline ${scrolled || !isHome ? 'text-foreground-600' : 'text-white/80'}`}>
              高清视频聚合平台
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-3 py-2 text-sm rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  scrolled || !isHome
                    ? 'text-foreground-700 hover:text-primary-500 hover:bg-primary-50'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                <i className={`${cat.icon} mr-1`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <form onSubmit={handleSearch} className="relative hidden sm:block">
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onFocus={() => setShowSearchSuggest(true)}
                onBlur={() => setTimeout(() => setShowSearchSuggest(false), 200)}
                placeholder="搜索视频、剧集、电影..."
                className={`w-48 lg:w-64 h-9 pl-9 pr-4 text-sm rounded-full border transition-all duration-200 outline-none ${
                  scrolled || !isHome
                    ? 'bg-background-100 border-background-200 text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200'
                    : 'bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:bg-white/20 focus:border-white/40'
                }`}
              />
              <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm ${scrolled || !isHome ? 'text-foreground-400' : 'text-white/60'}`}></i>
              {showSearchSuggest && searchValue && (
                <div className="absolute top-full mt-1 left-0 right-0 bg-background-50 border border-background-200 rounded-xl shadow-lg p-2">
                  <div className="text-xs text-foreground-500 px-3 py-1">搜索 "{searchValue}" 的结果</div>
                </div>
              )}
            </form>

            <Link
              to="/about"
              className={`text-sm whitespace-nowrap cursor-pointer transition-colors hidden sm:inline ${
                scrolled || !isHome ? 'text-foreground-600 hover:text-primary-500' : 'text-white/80 hover:text-white'
              }`}
            >
              关于我们
            </Link>

            <button className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-user-line text-white text-sm"></i>
            </button>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center cursor-pointer"
            >
              <i className={`text-xl ${scrolled || !isHome ? 'text-foreground-800' : 'text-white'} ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200 py-3 px-4 max-w-[1400px] mx-auto">
            <div className="grid grid-cols-4 gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className={`${cat.icon} text-lg text-primary-500`}></i>
                  <span className="text-xs text-foreground-700">{cat.name}</span>
                </Link>
              ))}
            </div>
            <Link
              to="/about"
              className="block text-center mt-3 py-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
            >
              关于我们
            </Link>
          </div>
        )}
      </nav>
    </header>
  );
}